/**
 * The call-placement test's QA preview (owner, 2026-11-05: test both sides in production). Dependency-free because
 * App.tsx reads it at startup: the app rewrites its address soon after, so the parameter is read once, as the app
 * starts, and kept for the tab.
 *
 *   ?call_placement_preview=in_composer | beside_composer   shows that side on every study guide in this tab
 *   ?call_placement_preview=off                             forgets it
 *
 * A previewed side is never recorded or reported as an exposure (studyGuideCallPlacementExperiment.ts).
 */
export const CALL_PLACEMENT_PREVIEW_URL_PARAMETER = "call_placement_preview";
const STORAGE_KEY = "study_guide_call_placement_preview";
const PREVIEWABLE_SIDES = ["beside_composer", "in_composer"] as const;
export type PreviewableCallPlacement = (typeof PREVIEWABLE_SIDES)[number];

let previewKeptInMemory: PreviewableCallPlacement | null = null;

function asSide(value: string | null): PreviewableCallPlacement | null {
  return PREVIEWABLE_SIDES.find(side => side === value) ?? null;
}

export function rememberCallPlacementPreviewAtAppStartup(search: string): void {
  const requested = new URLSearchParams(search).get(CALL_PLACEMENT_PREVIEW_URL_PARAMETER);
  if (requested === null) return;
  previewKeptInMemory = asSide(requested);
  try {
    if (previewKeptInMemory) sessionStorage.setItem(STORAGE_KEY, previewKeptInMemory);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable (some WebViews): the in-memory value covers this page load.
  }
}

export function readCallPlacementPreview(): PreviewableCallPlacement | null {
  if (previewKeptInMemory) return previewKeptInMemory;
  try {
    return asSide(sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}
